<script setup lang="ts">
import eventBus from '@/classes/EventBus'
import { formatCurrency } from '@/classes/Helpers'
import CheckoutModal from '@/components/checkout/CheckoutModal.vue'
import Order from '@/components/checkout/Order.vue'
import { useViewportSize } from '@/components/composables/useViewportSize'
import { API } from '@/modules/api'
import { useStore } from '@/stores'
import { useCartStore } from '@/stores/cart'
import { useTuitionContractStore } from '@/stores/tuitionContract'
import { useToast } from 'primevue'
import type { PropType } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
import { checkoutRequestDistrictId } from '@/components/checkout/checkoutRequest'
import { useI18n } from 'vue-i18n'
import PaymentFormatter from '@/classes/PaymentFormatter'
import { OrderItemType } from '@/types/orderItemType'
// Props
const props = defineProps({
  district: {
    type: Object as PropType<TDistrict>,
    required: true
  }
})

// Hooks
const toast = useToast()
const i18n = useI18n()
const store = useStore()
const cartStore = useCartStore()
const tuitionContractStore = useTuitionContractStore()
const { isMdOrGreater } = useViewportSize()

// Refs
const loading = ref(false)
const errorShown = ref(false)
const showCheckoutModal = ref(false)
const checkoutInProgress = ref(false)

// Computed
const selectedFundingSource = computed(() =>
  cartStore.getSelectedFundingSource(props.district.districtId)
)
const fundingSources = computed(() => store.getFundingSources(props.district.districtId))
const sortedFundingSources = computed(() => {
  return [...fundingSources.value].sort((a, b) => {
    if (a.type === b.type) {
      return parseInt(a.last4) - parseInt(b.last4)
    }
    return a.type === 'credit_card' ? -1 : 1
  })
})
const orderEstimate = computed(() => cartStore.getOrderEstimate(props.district.districtId))
const orderEstimateTotal = computed(() => {
  let amount = 0
  if (!orderEstimate.value) return amount
  if (orderEstimate.value === null) {
    return amount
  }
  orderEstimate.value.forEach((order: any) => {
    order.forEach((item: any) => {
      amount += item.amount
    })
  })
  return amount
})
const formattedOrderEstimateTotal = computed(() => formatCurrency(orderEstimateTotal.value))
const orderEstimateIsLoading = computed(() =>
  cartStore.getOrderEstimateLoading(props.district.districtId)
)

const payments = computed(() => {
  return PaymentFormatter.format(cartStore.getCartLines(props.district.districtId))
})

const selectedFundingSourceId = computed<number | null>(() => {
  const districtId = props.district.districtId
  const storedId = cartStore.selectedFundingSourceId?.[districtId] ?? null
  if (storedId) return storedId
  return sortedFundingSources.value.length > 0
    ? sortedFundingSources.value[0].fundingSourceId
    : null
})

// Filter out processing fees from the order estimate for display in Cart.vue
const orderEstimateWithoutProcessingFees = computed(() => {
  const estimate = orderEstimate.value
  if (!estimate) return null
  return estimate
    .map((order: any) =>
      order.filter((item: any) => item.orderItemType !== OrderItemType.ProcessingFee)
    )
    .filter((order: any[]) => order.length > 0)
})

// Calculate total without processing fees for Cart.vue display
const orderEstimateTotalWithoutFees = computed(() => {
  let amount = 0
  if (!orderEstimateWithoutProcessingFees.value) return amount
  orderEstimateWithoutProcessingFees.value.forEach((order: any) => {
    order.forEach((item: any) => {
      amount += item.amount
    })
  })
  return amount
})

const formattedOrderEstimateTotalWithoutFees = computed(() =>
  formatCurrency(orderEstimateTotalWithoutFees.value)
)

// Methods
const openCheckoutModal = () => {
  emit('set-cart-auto-hide', false)
  cartStore.fetchOrderEstimate(props.district.districtId)
  showCheckoutModal.value = true
}

const selectFundingSource = (fundingSourceId: number) => {
  cartStore.selectFundingSource(props.district.districtId, fundingSourceId)
}

const emit = defineEmits(['close-dropdown', 'set-cart-auto-hide'])

// Lifecycle hooks

const closeCheckoutModal = () => {
  showCheckoutModal.value = false
  emit('set-cart-auto-hide', true)
}

const removeItem = (item: TOrderItem) => {
  // if is tuition contract and not an invoice, remove from tuition contract store so it will remove invalid cart lines
  if (item.orderItemType === OrderItemType.TuitionContract && !item.isInvoice) {
    tuitionContractStore.removeFromCart(
      props.district.districtId,
      parseInt(item.uniqueId.split('::')[0]),
      parseInt(item.uniqueId.split('::')[1])
    )
  } else {
    cartStore.removeCartLine(props.district.districtId, item.uniqueId)
  }
}

const checkout = () => {
  checkoutInProgress.value = true
  const toastGroup = 'processingToastGroup' // Group name

  // Show initial processing toast
  const isZeroDollar = orderEstimateTotal.value === 0
  toast.add({
    group: toastGroup,
    severity: 'info',
    summary: isZeroDollar ? i18n.t('c.cart.processingOrder') : i18n.t('c.cart.processingPayment'),
    detail: isZeroDollar
      ? i18n.t('c.cart.processingOrderDetail')
      : i18n.t('c.cart.processingPaymentDetail'),
    life: 9000000
  })

  let data = {
    funding_source_id: isZeroDollar ? null : selectedFundingSourceId.value,
    payments: payments.value
  }

  API.Post.checkout(props.district.districtId, data)
    .then(({ success, partial_checkout, updated_data, errors }) => {
      checkoutInProgress.value = false
      if (success) {
        if (partial_checkout) {
          toast.removeGroup(toastGroup)
          toast.add({
            group: toastGroup,
            severity: 'warn',
            summary: i18n.t('c.fundingForms.transitDialog.success'),
            detail: i18n.t('c.cart.partialPaymentSuccess'),
            life: 5000
          })
        } else {
          toast.removeGroup(toastGroup)
          toast.add({
            severity: 'success',
            summary: i18n.t('c.fundingForms.transitDialog.success'),
            detail: isZeroDollar
              ? i18n.t('c.cart.paymentSuccess.1')
              : i18n.t('c.cart.paymentSuccess.2'),
            life: 5000
          })
        }
        //Emit event bus to re-fetch balances of all fees
        eventBus.emit('CHECKOUT_COMPLETED', { districtId: props.district.districtId })
        cartStore.clearCart(props.district.districtId)
        store.fetchDistrictStoreData(props.district.districtId)
        emit('close-dropdown')
      } else {
        toast.removeGroup(toastGroup)
        toast.add({
          severity: 'error',
          summary: i18n.t('c.fundingForms.transitDialog.error'),
          detail: i18n.t('c.cart.checkoutFailed'),
          life: 5000
        })
      }
      errors?.forEach(error => {
        console.error('Error from server: ' + error)
        toast.add({
          severity: 'error',
          summary: i18n.t('c.fundingForms.transitDialog.error'),
          detail: error.message,
          life: 5000
        })
      })
    })
    .catch(err => {
      checkoutInProgress.value = false
      toast.removeGroup(toastGroup)
      toast.add({
        severity: 'error',
        summary: i18n.t('c.fundingForms.transitDialog.error'),
        detail: i18n.t('c.cart.checkoutFailed'),
        life: 5000
      })
      // checkout() rethrows the backend's CheckoutError[] array on HTTP
      // failure (see src/modules/api/post/checkout.ts). The pre-DTB-22 code
      // here read `err.responseJSON.errors` (jQuery $.ajax shape, never
      // populated under axios), which silently threw a TypeError and left
      // the family seeing only the generic "Checkout failed" toast with no
      // per-exception detail.
      if (Array.isArray(err) && err.length > 0) {
        err.forEach((error: { message: string }) => {
          console.error('Error from server: ' + JSON.stringify(error))
          toast.add({
            severity: 'error',
            summary: i18n.t('c.fundingForms.transitDialog.error'),
            detail: error.message,
            life: 5000
          })
        })
      } else {
        console.error(err)
        toast.add({
          severity: 'error',
          summary: i18n.t('c.fundingForms.transitDialog.error'),
          detail: i18n.t('c.cart.unknownError'),
          life: 5000
        })
      }
    })
}

onMounted(() => {
  cartStore.fetchOrderEstimate(props.district.districtId)
})

// DTA-1036: another page asked for checkout in this district (requestCheckout).
watch(
  checkoutRequestDistrictId,
  districtId => {
    if (districtId !== props.district.districtId) return
    checkoutRequestDistrictId.value = null
    openCheckoutModal()
  },
  { immediate: true }
)
</script>

<template>
  <div class="Cart">
    <CheckoutModal
      v-model="showCheckoutModal"
      :district="district"
      :order-estimate="orderEstimate"
      :funding-sources="sortedFundingSources"
      :selected-funding-source-id="selectedFundingSourceId"
      :checkout-in-progress="checkoutInProgress"
      :loading-order-estimate="orderEstimateIsLoading"
      @select-funding-source="selectFundingSource"
      @checkout="checkout"
      @close-checkout-modal="closeCheckoutModal"
    />
    <!--Declare processing toast with the group id so that it could be replaced by other toast messages -->
    <Toast group="processingToastGroup" />
    <div :style="isMdOrGreater ? { width: '500px' } : { maxWidth: '500px' }">
      <div v-if="orderEstimateIsLoading" class="text-center font-bold" style="min-height: 75px">
        <ProgressSpinner
          size="small"
          stroke-width="5"
          style="width: 50px; height: 50px"
          aria-label="Loading"
        />
      </div>

      <div v-else>
        <div class="grid font-bold">
          <div class="col-9 mt-2">{{ district.districtName }} ({{ district.districtId }})</div>
          <div class="col-3">
            <Button size="small" @click="openCheckoutModal">{{
              $t('c.nav.navData.label.cart')
            }}</Button>
          </div>
        </div>

        <order
          v-for="(order, index) in orderEstimateWithoutProcessingFees"
          :order-number="Number(index) + 1"
          :order="order.flat()"
          :key="`order-${index}`"
          @remove-item="removeItem"
        />
        <Divider />
        <div class="grid mb-3 font-bold">
          <div class="col-7">{{ $t('c.checkout.order.grandTotalLabel') }}:</div>
          <div class="col-3 flex align-items-center justify-content-end">
            {{ formattedOrderEstimateTotalWithoutFees }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.Cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}
</style>
